import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Box, Button, Grid, IconButton, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';

import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import BusTourListMobileLayout, { BusTourMobileActionButton, BusTourMobileListItem } from './BusTourListMobile';
import BusTourListSearchField, { busTourListToolbarButtonSx, busTourListToolbarRowSx } from './BusTourListSearchField';
import { useBusTourAddLabel } from './useBusTourAddLabel';
import VehicleEditDialog from './VehicleEditDialog';

const useStyles = makeStyles((theme) => ({
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
}));

const formatStatusTs = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const VehicleList = (props) => {
    const history = useHistory();
    const { hasPerm } = React.useContext(ContextStore);
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');

    const { vehicleId: urlVehicleId } = useParams();

    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const urlParams = new URLSearchParams(window.location.search);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const [editDialogOpen, setEditDialogOpen] = React.useState(!!urlVehicleId);
    const [editVehicleId, setEditVehicleId] = React.useState(urlVehicleId ? Number(urlVehicleId) : null);
    const addButtonLabel = useBusTourAddLabel('NEW BUS');

    const resource = 'vehicle';

    const onQueryChange = (newQuery) => {
        setQuery(newQuery);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', newQuery);
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['bus', 'buses'], onDeleted: refreshList });
    const doDelete = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setSelectedIds([]);
                    setIdsToDelete(null);
                }
            })
            .finally(() => setSyncActive(false));
    };

    const openAddDialog = () => {
        setEditVehicleId(null);
        setEditDialogOpen(true);
    };

    const openEditDialog = React.useCallback(
        (id) => {
            setEditVehicleId(id);
            setEditDialogOpen(true);
            history.replace(`/bus-tours/vehicles/${id}`);
        },
        [history]
    );

    const onDialogClose = (saved) => {
        setEditDialogOpen(false);
        setEditVehicleId(null);
        history.replace('/bus-tours/vehicles');
        if (saved) {
            setLastUpdate(new Date().toString());
        }
    };

    const listItemRenderer = React.useCallback(
        (row, idx, rows) => {
            const openRow = () => openEditDialog(row.id);
            const status = row.enabled === false ? 'Inactive' : row.status || 'Active';
            const statusTs = formatStatusTs(row.status_ts);
            const latNum = row.latitude == null ? NaN : Number(row.latitude);
            const lngNum = row.longitude == null ? NaN : Number(row.longitude);
            const hasCoords = !Number.isNaN(latNum) && !Number.isNaN(lngNum);
            const routeSet = row.route_set;
            const routeTarget = routeSet
                ? row.route?.id
                    ? `/bus-tours/line-sets/${routeSet.id}/edit?route=${row.route.id}`
                    : `/bus-tours/line-sets/${routeSet.id}/edit`
                : null;

            return (
                <BusTourMobileListItem
                    key={row.id}
                    name={row.name}
                    onOpen={openRow}
                    isLast={idx >= rows.length - 1}
                    details={
                        <>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Bus ID: {row.id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Licence Plate: {row.license_plate || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Live Status: {status}
                                {statusTs ? ` · ${statusTs}` : ''}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Coordinates:{' '}
                                {hasCoords ? (
                                    <Link
                                        href={`https://www.google.com/maps?q=${latNum},${lngNum}`}
                                        target="_blank"
                                        onClick={(ev) => ev.stopPropagation()}
                                    >
                                        {`${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`}
                                    </Link>
                                ) : (
                                    '—'
                                )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Line Set / Line:{' '}
                                {routeTarget ? (
                                    <Link component={RouterLink} to={routeTarget} onClick={(ev) => ev.stopPropagation()}>
                                        {routeSet.name}
                                        {row.route?.name ? ` / ${row.route.name}` : ''}
                                    </Link>
                                ) : (
                                    '—'
                                )}
                            </Typography>
                        </>
                    }
                    actions={
                        <>
                            <Grid item>
                                <IconButton
                                    size="small"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        openEditDialog(row.id);
                                    }}
                                    aria-label="Edit bus"
                                >
                                    <BorderColorOutlinedIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton
                                    title="Delete bus"
                                    perm="vehicle_fleet.delete"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        setIdsToDelete([row.id]);
                                    }}
                                />
                            </Grid>
                        </>
                    }
                />
            );
        },
        [openEditDialog]
    );

    const vehiclesRecordList = (
        <RecordList
            className={classes.recordList}
            resource={resource}
            disableSelectionOnClick
            sortModel={[{ field: 'id', sort: 'desc' }]}
            deps={lastUpdate}
            query={query}
            selectionModel={selectedIds}
            onSelectionModelChange={(selectionModel, selection) => setSelectedIds(selection || [])}
            listItemRenderer={isSmallScreen ? listItemRenderer : undefined}
            mobileNaturalHeight={isSmallScreen}
            noMatchesLabel="No buses"
            noItemsFoundMessage="No buses"
            initialState={{
                columns: { columnVisibilityModel: { lat_lng: true } },
            }}
            columns={[
                {
                    field: 'id',
                    headerName: 'Bus ID',
                    sortable: true,
                    width: 100,
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    sortable: true,
                    flex: 0.5,
                },
                {
                    field: 'license_plate',
                    headerName: 'Licence Plate',
                    sortable: true,
                    flex: 0.5,
                },
                {
                    field: 'status',
                    headerName: 'Live Status',
                    sortable: false,
                    width: 120,
                    valueGetter: (p) => {
                        if (p.row.enabled === false) return 'Inactive';
                        return p.row.status || 'Active';
                    },
                },
                {
                    field: 'status_ts',
                    headerName: 'Status updated',
                    sortable: false,
                    filterable: false,
                    width: 180,
                    valueGetter: (p) => formatStatusTs(p.row.status_ts),
                },
                {
                    field: 'lat_lng',
                    headerName: 'Coordinates',
                    sortable: false,
                    filterable: false,
                    width: 200,
                    renderCell: (p) => {
                        const lat = p.row.latitude;
                        const lng = p.row.longitude;
                        if (lat == null || lng == null) return '-';
                        const latNum = Number(lat);
                        const lngNum = Number(lng);
                        if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return '-';
                        const href = `https://www.google.com/maps?q=${latNum},${lngNum}`;
                        return (
                            <Link
                                href={href}
                                target="_blank"
                            >
                                {`${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`}
                            </Link>
                        );
                    },
                },
                {
                    field: 'route_set',
                    headerName: 'Line Set / Line',
                    sortable: false,
                    flex: 1.1,
                    renderCell: (p) => {
                        const routeSet = p.row.route_set;
                        const route = p.row.route;
                        if (!routeSet) return '-';
                        // Deep-link to the specific line tab via the URL
                        // schema implemented in LineSetEdit (?route=<id>).
                        const target = route?.id
                            ? `/bus-tours/line-sets/${routeSet.id}/edit?route=${route.id}`
                            : `/bus-tours/line-sets/${routeSet.id}/edit`;
                        return (
                            <Link
                                component={RouterLink}
                                to={target}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {routeSet.name}
                                {route?.name ? ` / ${route.name}` : ''}
                            </Link>
                        );
                    },
                },
                {
                    field: 'buttons',
                    headerName: 'Action',
                    sortable: false,
                    width: 100,
                    renderCell: (p) => (
                        <Grid container alignItems="center">
                            <Grid item>
                                <IconButton size="small" onClick={() => openEditDialog(p.id)}>
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton perm="vehicle_fleet.delete" onClick={() => setIdsToDelete([p.id])} />
                            </Grid>
                        </Grid>
                    ),
                },
            ]}
        />
    );

    return (
        <>
            <PageMeta title="Buses" />
            {idsToDelete && (
                <ConfirmationDialog
                    keepMounted
                    open={!!idsToDelete}
                    onCancel={() => setIdsToDelete(null)}
                    onConfirm={() => doDelete(idsToDelete)}
                    title={idsToDelete.length === 1 ? 'Delete bus?' : `Delete ${idsToDelete.length} buses?`}
                    okButtonText="Delete"
                    cancelButtonText="Cancel"
                >
                    {idsToDelete.length === 1
                        ? 'Are you sure you want to delete this bus?'
                        : `Are you sure you want to delete ${idsToDelete.length} buses?`}
                </ConfirmationDialog>
            )}
            <FixedLinearProgress visibility={syncActive} />
            <VehicleEditDialog open={editDialogOpen} vehicleId={editVehicleId} onClose={onDialogClose} />
            {isSmallScreen ? (
                <BusTourListMobileLayout
                    title="Buses"
                    searchLabel="Search Buses"
                    searchValue={query || ''}
                    onSearchChange={(e) => onQueryChange(e.target.value)}
                    actionButton={
                        <BusTourMobileActionButton
                            selectedCount={selectedIds.length}
                            canDelete={hasPerm('vehicle_fleet', 'delete')}
                            onDelete={() => setIdsToDelete(selectedIds)}
                            onAdd={openAddDialog}
                            addLabel={addButtonLabel}
                            deleteLabel="Delete selected buses"
                        />
                    }
                >
                    {vehiclesRecordList}
                </BusTourListMobileLayout>
            ) : (
                <Grid container direction="row" spacing={2} p={2}>
                    <Grid item xs={12} sx={{ pb: '0 !important' }}>
                        <PageHeading sx={{ mb: 1.5 }}>Buses</PageHeading>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '0 !important' }}>
                        <Box sx={busTourListToolbarRowSx}>
                            <BusTourListSearchField
                                value={query || ''}
                                onChange={(e) => onQueryChange(e.target.value)}
                            />
                            {selectedIds.length > 0 ? (
                                <DeleteButton
                                    perm="vehicle_fleet.delete"
                                    onClick={() => setIdsToDelete(selectedIds)}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {selectedIds.length === 1 ? 'DELETE BUS' : `DELETE ${selectedIds.length} BUSES`}
                                </DeleteButton>
                            ) : (
                                <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} sx={busTourListToolbarButtonSx}>
                                    {addButtonLabel}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        {vehiclesRecordList}
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default VehicleList;
